import { format } from "date-fns";
import { AlertCircle, ChevronDown, PlusCircle, PlusIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { DateRange } from "react-day-picker";
import { Link, Navigate } from "react-router-dom";

import { UpgradeCard } from "@/app/components/upgrade-card";
import { SearchBar } from "@/components/search-bar";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { ComboBox } from "@/components/ui/combo-box";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { TabNavigation } from "@/components/ui/tab-navigation";
import { useInfiniteEvents } from "@/features/events/api/get-events";
import { EventItem } from "@/features/events/components/event-item";
import { EventItemSkeleton } from "@/features/events/components/event-item-skeleton";
import { NoEvents } from "@/features/events/components/no-events";
import { OnboardingHeader } from "@/features/registration/components/onboarding-header";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { cn } from "@/lib/utils";
import {
  EventCategory,
  EventType,
  RoleEnum,
  RoleGroupEnum,
} from "@classview/shared-types";

export function EventsPage() {
  const { hasPermission, organisation } = useOrganisation();
  const [search, setSearch] = useState("");
  const [type, setType] = useState<EventType>();
  const [category, setCategory] = useState<EventCategory>();
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [date, setDate] = useState<DateRange>();

  const {
    data,
    isLoading,
    isError,
    error,
    isSuccess,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteEvents({
    organisationId: organisation.id,
    limit: 9,
    search,
    type,
    category,
    startDate: date && date.from ? format(date.from, "yyyy-MM-dd") : undefined,
    endDate: date && date.to ? format(date.to, "yyyy-MM-dd") : undefined,
  });

  const events = useMemo(
    () => (isSuccess ? data.pages.flatMap((page) => page.items) : []),
    [isSuccess, data?.pages],
  );

  if (isError)
    return (
      <section>
        <OnboardingHeader
          title="Events"
          description="View your organisations events"
          className="mb-6"
        />
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {error?.message ?? "An unexpected error occurred"}
          </AlertDescription>
        </Alert>
      </section>
    );

  const typeOptions = Object.values(EventType).map((type) => ({
    value: type,
    label: type.toLowerCase(),
  }));

  const categoryOptions = Object.values(EventCategory).map((category) => ({
    value: category,
    label: category.toLowerCase(),
  }));

  const displayUpgradeCard =
    !hasPermission([RoleEnum.EVENTS_CREATE]) &&
    events.length === 0 &&
    (organisation.roleGroup === RoleGroupEnum.ADMIN ||
      organisation.roleGroup === RoleGroupEnum.USER);

  const isSessionGuest = organisation.roleGroup === RoleGroupEnum.SESSION_GUEST;
  /**
   * Session guest users have no access to all events page
   */
  if (isSessionGuest) {
    return <Navigate to="/events/my" replace />;
  }

  return (
    <div>
      <OnboardingHeader
        title="Events"
        className="mb-6"
        actions={
          hasPermission(RoleEnum.EVENTS_CREATE) && (
            <Button asChild>
              <Link to={`/events/create`}>
                <PlusIcon className="mr-1 size-4 text-inherit" /> New Event
              </Link>
            </Button>
          )
        }
      />

      {!isSessionGuest && (
        <TabNavigation
          className="my-6 md:my-10"
          options={[
            { label: "Discover Events", to: "/events/discover" },
            { label: "My Events", to: "/events/my" },
          ]}
        />
      )}

      <div className="mb-6 flex items-center gap-4">
        <div className="max-w-[320px]">
          <SearchBar
            search={search}
            setSearch={(value) => setSearch((value as string) || "")}
            className="mb-2 sm:mb-0 sm:max-w-xs"
            placeholder="Search events"
          />
        </div>

        <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
          <PopoverTrigger asChild>
            <Button
              variant={date ? "default" : "outline"}
              className={cn(
                "gap-2",
                !date?.from && !date?.to && "text-muted-foreground",
              )}
            >
              <PlusCircle className="size-4" />
              {date?.from && date?.to
                ? format(date.from, "EEE dd MMM, yyyy") +
                  " - " +
                  format(date.to, "EEE dd MMM, yyyy")
                : "Date"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar
              mode="range"
              selected={date}
              onSelect={(date) => {
                setDate(date);

                if (date?.from && date?.to) {
                  setIsDatePickerOpen(false);
                }
              }}
            />
          </PopoverContent>
        </Popover>
        <ComboBox
          options={categoryOptions}
          triggerText="Category"
          leadingIcon={<PlusCircle className="size-4" />}
          value={category}
          onChange={(value) => setCategory((value as EventCategory) || null)}
        />
        <ComboBox
          options={typeOptions}
          triggerText="Type"
          leadingIcon={<PlusCircle className="size-4" />}
          value={type?.toString()}
          onChange={(value) => setType((value as EventType) || null)}
        />
      </div>

      {displayUpgradeCard && !isLoading && (
        <div className="max-w-xl">
          <UpgradeCard
            title="Hosting Events?"
            description="Reach out to our sales team who can take you through the benefits of signing up for our events package."
            buttonText="Get in Touch"
          />
        </div>
      )}

      {events.length === 0 && !displayUpgradeCard && !isLoading && <NoEvents />}

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {events.map((event) => (
          <EventItem key={event.id} event={event} />
        ))}

        {isLoading && (
          <>
            <EventItemSkeleton />
            <EventItemSkeleton />
            <EventItemSkeleton />
          </>
        )}
      </div>
      {hasNextPage && (
        <div className="flex justify-center">
          <Button
            onClick={() => fetchNextPage()}
            type="button"
            disabled={isFetchingNextPage}
            isLoading={isFetchingNextPage}
            isAnimated={isFetchingNextPage}
            variant="link"
          >
            {isFetchingNextPage ? (
              "Loading..."
            ) : (
              <>
                <ChevronDown className="mr-2 size-4" />
                Load More
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
